import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { AuthorizationService } from '@myarvato/core/authentication';
import { FilterRecord, KpiStateDto, MyArvatoEnvironment } from '@myarvato/shared/models';
import { UserLayoutDto } from '@myarvato/core/customization';
import { ViewItem } from '../models';

interface ViewValue {
  filters?: FilterRecord;
  sortField?: string;
  sortOrder?: number;
  rows?: number;
  columns?: string[];
  kpis?: KpiStateDto;
}

interface CreateViewRequest {
  name: string;
  context: string;
  value: ViewValue;
  isShared: boolean;
}

interface UpdateViewRequest {
  id: string;
  name: string;
  context: string;
  isShared: boolean;
  value: ViewValue;
}

export interface TrackingListViewState {
  views: ViewItem[];
  selectedViewId: string | null;
  loadingStates: {
    views: boolean;
    saving: boolean;
    deleting: string | null;
    updating: string | null;
  };
  error: string | null;
}

@Injectable()
export class ViewDataService {
  private readonly http = inject(HttpClient);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly environment = inject(MyArvatoEnvironment);

  private readonly SESSION_STORAGE_KEY = 'transport-tracking-list-selected-view';

  private readonly apiConfig = {
    baseUrl: `${this.environment.url.api}Customization/UserLayouts/Layout`,
    context: 'transport',
  };

  // === STATE ===
  private readonly _state = signal<TrackingListViewState>({
    views: [],
    selectedViewId: this.loadSelectedView(),
    loadingStates: {
      views: false,
      saving: false,
      deleting: null,
      updating: null,
    },
    error: null,
  });

  // === SELECTORS ===
  public readonly state = this._state.asReadonly();
  public readonly views = computed(() => this._state().views);
  public readonly selectedViewId = computed(() => this._state().selectedViewId);
  public readonly loadingStates = computed(() => this._state().loadingStates);
  public readonly isLoading = computed(() => {
    const loading = this._state().loadingStates;
    return loading.views || loading.saving || !!loading.deleting || !!loading.updating;
  });
  public readonly error = computed(() => this._state().error);
  public readonly standardViews = computed(() => this.views().filter((view) => view.isShared));
  public readonly userViews = computed(() => this.views().filter((view) => !view.isShared));
  public readonly isAdmin = computed(() => this.authorizationService.hasRole('admin'));
  public readonly selectedView = computed(() => {
    const viewId = this.selectedViewId();
    return viewId ? this.views().find((view) => view.id === viewId) : undefined;
  });

  constructor() {
    this.loadUserViews();
  }

  private loadSelectedView(): string | null {
    const disabledSelectedViewLoading = this.loadSelectedViewDisabledFromRouterState();
    if (disabledSelectedViewLoading) {
      return null;
    }

    return this.loadSelectedViewFromRouterState() ?? this.loadSelectedViewFromSessionStorage();
  }

  private loadSelectedViewDisabledFromRouterState(): boolean {
    const routerState = history.state['disable-selected-view'] as boolean;
    if (routerState) {
      return routerState;
    }

    return false;
  }

  private loadSelectedViewFromRouterState(): string | null {
    const routerState = history.state['selected-view'] as UserLayoutDto<unknown>;

    if (routerState && routerState.id) {
      let existingSessionValue: string | null = null;
      try {
        existingSessionValue = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
      } catch {
        console.log('Failed to access session storage');
      }

      if (existingSessionValue !== routerState.id) {
        this.saveSelectedViewToSessionStorage(routerState.id);
      }

      try {
        history.replaceState({ ...history.state, 'selected-view': undefined }, '');
      } catch {
        console.log('Failed to clear selected view from router state');
      }

      return routerState.id;
    }
    return null;
  }

  private loadSelectedViewFromSessionStorage(): string | null {
    try {
      return sessionStorage.getItem(this.SESSION_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private saveSelectedViewToSessionStorage(viewId: string | null): void {
    try {
      if (viewId) {
        sessionStorage.setItem(this.SESSION_STORAGE_KEY, viewId);
      } else {
        sessionStorage.removeItem(this.SESSION_STORAGE_KEY);
      }
    } catch {
      console.log('Failed to save selected view to session storage');
    }
  }

  // === ACTIONS ===
  public selectView(viewId: string): void {
    try {
      if (history.state && history.state['selected-view']) {
        history.replaceState({ ...history.state, 'selected-view': undefined }, '');
      }
    } catch {
      // Ignore history manipulation errors
    }

    this._state.update((state) => ({
      ...state,
      selectedViewId: viewId,
      error: null,
    }));

    this.saveSelectedViewToSessionStorage(viewId);
  }

  public saveView(
    name: string,
    filters?: FilterRecord,
    sortField?: string,
    sortOrder?: number,
    rows?: number,
    columns?: string[],
    isStandardView?: boolean,
    kpis?: KpiStateDto,
  ): void {
    this._state.update((state) => ({
      ...state,
      loadingStates: { ...state.loadingStates, saving: true },
      error: null,
    }));

    const createRequest: CreateViewRequest = {
      name: name.trim(),
      context: this.apiConfig.context,
      value: {
        filters: filters ? { ...filters } : undefined,
        sortField,
        sortOrder,
        rows: rows ?? 10,
        columns: columns ? [...columns] : undefined,
        kpis: kpis ?? undefined,
      },
      isShared: isStandardView ?? false,
    };

    this.saveViewToApi(createRequest).subscribe({
      next: (savedView) => {
        const viewItem = this.mapApiViewToViewItem(savedView);
        this._state.update((state) => ({
          ...state,
          views: [...state.views, viewItem],
          selectedViewId: viewItem.id,
          loadingStates: { ...state.loadingStates, saving: false },
          error: null,
        }));
        this.saveSelectedViewToSessionStorage(viewItem.id);
      },
      error: (error) => {
        console.error('Failed to save view:', error);
        this._state.update((state) => ({
          ...state,
          loadingStates: { ...state.loadingStates, saving: false },
          error: 'Failed to save view',
        }));
      },
    });
  }

  public updateView(
    viewId: string,
    name: string,
    filters?: FilterRecord,
    sortField?: string,
    sortOrder?: number,
    rows?: number,
    columns?: string[],
    kpis?: KpiStateDto,
  ): void {
    const existingView = this.getUserView(viewId);
    if (!existingView || !existingView.isMy) {
      this._state.update((state) => ({
        ...state,
        error: 'Cannot update this view',
      }));
      return;
    }

    this._state.update((state) => ({
      ...state,
      loadingStates: { ...state.loadingStates, updating: viewId },
      error: null,
    }));

    const updateRequest: UpdateViewRequest = {
      id: viewId,
      name: name.trim(),
      context: this.apiConfig.context,
      isShared: existingView.isShared,
      value: {
        filters: filters ? { ...filters } : undefined,
        sortField,
        sortOrder,
        rows: rows ?? existingView.value.rows,
        columns: columns ? [...columns] : existingView.value.columns,
        kpis: kpis ?? undefined,
      },
    };

    this.updateViewInApi(updateRequest).subscribe({
      next: (updatedView) => {
        const viewItem = this.mapApiViewToViewItem(updatedView);
        this._state.update((state) => ({
          ...state,
          views: state.views.map((view) => (view.id === viewId ? viewItem : view)),
          loadingStates: { ...state.loadingStates, updating: null },
          error: null,
        }));
      },
      error: (error) => {
        console.error('Failed to update view:', error);
        this._state.update((state) => ({
          ...state,
          loadingStates: { ...state.loadingStates, updating: null },
          error: 'Failed to update view',
        }));
      },
    });
  }

  public deleteView(viewId: string): void {
    const viewToDelete = this.getUserView(viewId);
    if (!viewToDelete) {
      this._state.update((state) => ({
        ...state,
        error: 'View not found',
      }));
      return;
    }

    const canDelete =
      (viewToDelete.isMy && !viewToDelete.isShared) || (viewToDelete.isShared && this.authorizationService.hasRole('admin'));
    if (!canDelete) {
      this._state.update((state) => ({
        ...state,
        error: 'Cannot delete this view',
      }));
      return;
    }

    this._state.update((state) => ({
      ...state,
      loadingStates: { ...state.loadingStates, deleting: viewId },
      error: null,
    }));

    this.deleteViewFromApi(viewId).subscribe({
      next: () => {
        this._state.update((state) => {
          const wasSelected = state.selectedViewId === viewId;
          const newState = {
            ...state,
            views: state.views.filter((view) => view.id !== viewId),
            selectedViewId: wasSelected ? null : state.selectedViewId,
            loadingStates: { ...state.loadingStates, deleting: null },
            error: null,
          };

          if (wasSelected) {
            this.saveSelectedViewToSessionStorage(null);
          }

          return newState;
        });
      },
      error: (error) => {
        console.error('Failed to delete view:', error);
        this._state.update((state) => ({
          ...state,
          loadingStates: { ...state.loadingStates, deleting: null },
          error: 'Failed to delete view',
        }));
      },
    });
  }

  public resetSelection(): void {
    this._state.update((state) => ({
      ...state,
      selectedViewId: null,
      error: null,
    }));

    this.saveSelectedViewToSessionStorage(null);
  }

  public getUserView(viewId: string): ViewItem | undefined {
    return this.views().find((view) => view.id === viewId);
  }

  private loadUserViews(): void {
    this._state.update((state) => ({
      ...state,
      loadingStates: { ...state.loadingStates, views: true },
      error: null,
    }));

    this.http.get<ViewItem[]>(`${this.apiConfig.baseUrl}?context=${this.apiConfig.context}`).subscribe({
      next: (apiViews) => {
        const userViews = (apiViews || []).map((apiView) => this.mapApiViewToViewItem(apiView));
        const allViews = [...userViews];

        this._state.update((state) => {
          const selectedViewId = state.selectedViewId;
          const validSelectedViewId = selectedViewId && allViews.some((view) => view.id === selectedViewId) ? selectedViewId : null;

          if (selectedViewId && !validSelectedViewId) {
            this.saveSelectedViewToSessionStorage(null);
          }

          return {
            ...state,
            views: allViews,
            selectedViewId: validSelectedViewId,
            loadingStates: { ...state.loadingStates, views: false },
            error: null,
          };
        });
      },
      error: (error) => {
        console.error('Failed to load views from API:', error);
        this._state.update((state) => ({
          ...state,
          loadingStates: { ...state.loadingStates, views: false },
          error: 'Failed to load views',
        }));
      },
    });
  }

  private saveViewToApi(createRequest: CreateViewRequest) {
    return this.http.post<ViewItem>(this.apiConfig.baseUrl, createRequest);
  }

  private updateViewInApi(updateRequest: UpdateViewRequest) {
    return this.http.put<ViewItem>(this.apiConfig.baseUrl, updateRequest);
  }

  private deleteViewFromApi(viewId: string) {
    return this.http.delete(`${this.apiConfig.baseUrl}?id=${viewId}`);
  }

  private mapApiViewToViewItem(apiView: ViewItem): ViewItem {
    return {
      id: apiView.id,
      name: apiView.name,
      value: {
        filters: apiView.value?.filters,
        sortOrder: apiView.value?.sortOrder,
        sortField: apiView.value?.sortField,
        rows: apiView.value?.rows,
        columns: apiView.value?.columns,
        kpis: apiView.value?.kpis,
      },
      isShared: apiView.isShared,
      created: typeof apiView.created === 'string' ? new Date(apiView.created) : apiView.created,
      updated: typeof apiView.updated === 'string' ? new Date(apiView.updated) : apiView.updated,
      isMy: apiView.isMy,
    };
  }
}
